import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { ModifierForm } from "./components/ModifierForm";
import {
  useGetModifierQuery,
  useCreateModifierMutation,
  useUpdateModifierMutation,
  useDeleteModifierMutation,
} from "../query/modifiersQuery";
import type { RestCreateModifier } from "@KassaroAB/shared/types/modifier";

const FORM_ID = "catalog-modifier-form";

export const ModifierFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("modifiers");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const modifierId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: modifier,
    isLoading,
    error: loadError,
  } = useGetModifierQuery(scope, modifierId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateModifierMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateModifierMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteModifierMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!modifierId && !isLoading && !loadError && !modifier;

  const handleSubmit = (data: RestCreateModifier) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!modifierId) return;
    updateMutation(
      { modifierId, modifier: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("modifiersView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!modifierId) return;
    deleteMutation(modifierId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("modifiersView.panelCreate")
    : (modifier?.name ?? t("modifiersView.title"));
  const breadcrumbCurrent = isCreating
    ? t("modifiersView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (modifier?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!modifier && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("modifiersView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("modifiersView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("modifiersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !modifier ? (
          <LoadingContainer message={t("modifiersView.loading")} />
        ) : missing ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : showForm ? (
          <>
            <ModifierForm
              key={modifier?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              modifier={isCreating ? undefined : modifier}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        ) : null}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("modifierForm.deleteModifier")}
        message={t("modifiersView.deleteConfirm", {
          name: modifier?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
